import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SERVICES, SERVICE_BY_KEY } from "@/lib/services";
import { toast } from "sonner";
import { CheckCircle, ArrowLeft, ArrowRight } from "@phosphor-icons/react";

const STEPS = ["Service", "Details", "Scope", "Review"];

export default function NewProject() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ title: "", service_type: "", description: "", budget: "", timeline: "" });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const canNext =
    (step === 0 && !!form.service_type) ||
    (step === 1 && form.title.trim().length > 2) ||
    (step === 2 && form.description.trim().length > 10) ||
    step === 3;

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Project submitted. We'll review it shortly.");
      nav(`/dashboard/projects/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally { setLoading(false); }
  };

  return (
    <AppShell subtitle="§ NEW BRIEF" title="Start a new project">
      {/* stepper */}
      <div className="grid grid-cols-4 gap-px bg-black/10 mb-8 border border-black/10">
        {STEPS.map((s, i) => (
          <div key={s} className={`p-4 bg-white ${i <= step ? "" : "opacity-50"}`} data-testid={`wizard-step-${i}`}>
            <div className="fx-mono-tag" style={{color: i <= step ? "#0B4DCC" : "#a3a3a3"}}>STEP {String(i+1).padStart(2,"0")}</div>
            <div className="mt-2 text-sm font-semibold tracking-tight">{s}</div>
            <div className={`mt-3 h-1 w-full ${i < step ? "bg-[#0B1F3A]" : i === step ? "bg-[#0B4DCC]" : "bg-[#EBEBEB]"}`} />
          </div>
        ))}
      </div>

      <div className="border border-black/10 p-8 lg:p-10 min-h-[380px]">
        {step === 0 && (
          <div>
            <h2 className="text-2xl font-bold tracking-tight mb-2">What kind of build?</h2>
            <p className="text-neutral-600 text-sm mb-6">Pick the service closest to what you need. You can refine later.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {SERVICES.map(s => (
                <button
                  key={s.key}
                  onClick={() => set("service_type", s.key)}
                  className={`text-left p-5 border rounded-md transition-all ${
                    form.service_type === s.key ? "border-[#0B4DCC] bg-[#EEF3FF]" : "border-black/10 hover:border-black/30"
                  }`}
                  data-testid={`wizard-service-${s.key}`}
                >
                  <div className="fx-mono-tag mb-2">{s.code}</div>
                  <div className="font-semibold tracking-tight">{s.name}</div>
                  <div className="text-xs text-neutral-500 mt-2 leading-relaxed">{s.blurb}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold tracking-tight mb-2">Give it a title.</h2>
            <p className="text-neutral-600 text-sm mb-6">Short and descriptive works best.</p>
            <Label className="fx-mono-tag">PROJECT TITLE</Label>
            <Input value={form.title} onChange={e=>set("title", e.target.value)} className="mt-2 h-12 rounded-md" placeholder="e.g. Booking system for dental clinic" data-testid="wizard-title-input" />
          </div>
        )}

        {step === 2 && (
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold tracking-tight mb-2">Describe the scope.</h2>
            <p className="text-neutral-600 text-sm mb-6">Outcome you want, existing tools, key constraints.</p>
            <Label className="fx-mono-tag">DESCRIPTION</Label>
            <Textarea value={form.description} onChange={e=>set("description", e.target.value)} className="mt-2 min-h-32 rounded-md" placeholder="What outcome are you trying to reach?" data-testid="wizard-description-input" />
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div>
                <Label className="fx-mono-tag">BUDGET (OPTIONAL)</Label>
                <Input value={form.budget} onChange={e=>set("budget", e.target.value)} className="mt-2 h-11 rounded-md" placeholder="$5k–15k" data-testid="wizard-budget-input" />
              </div>
              <div>
                <Label className="fx-mono-tag">TIMELINE (OPTIONAL)</Label>
                <Input value={form.timeline} onChange={e=>set("timeline", e.target.value)} className="mt-2 h-11 rounded-md" placeholder="4 weeks" data-testid="wizard-timeline-input" />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold tracking-tight mb-2 flex items-center gap-2"><CheckCircle size={22} weight="duotone" className="text-[#34C759]" /> Review your brief</h2>
            <p className="text-neutral-600 text-sm mb-6">Make sure everything looks right before submitting.</p>
            <dl className="border border-black/10 divide-y divide-black/5 text-sm">
              <div className="p-4 grid grid-cols-3"><dt className="fx-mono-tag col-span-1">SERVICE</dt><dd className="col-span-2 font-medium">{SERVICE_BY_KEY[form.service_type]?.name}</dd></div>
              <div className="p-4 grid grid-cols-3"><dt className="fx-mono-tag col-span-1">TITLE</dt><dd className="col-span-2 font-medium">{form.title}</dd></div>
              <div className="p-4 grid grid-cols-3"><dt className="fx-mono-tag col-span-1">DESCRIPTION</dt><dd className="col-span-2 whitespace-pre-wrap">{form.description}</dd></div>
              <div className="p-4 grid grid-cols-3"><dt className="fx-mono-tag col-span-1">BUDGET</dt><dd className="col-span-2">{form.budget || "—"}</dd></div>
              <div className="p-4 grid grid-cols-3"><dt className="fx-mono-tag col-span-1">TIMELINE</dt><dd className="col-span-2">{form.timeline || "—"}</dd></div>
            </dl>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={() => step === 0 ? nav(-1) : setStep(step - 1)} className="rounded-md h-11 border-black/20" data-testid="wizard-back-btn">
          <ArrowLeft size={14} weight="bold" className="mr-2" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button disabled={!canNext} onClick={() => setStep(step + 1)} className="fx-btn-primary rounded-md h-11 px-6" data-testid="wizard-next-btn">
            Continue <ArrowRight size={14} weight="bold" className="ml-2" />
          </Button>
        ) : (
          <Button disabled={loading} onClick={submit} className="fx-btn-accent rounded-md h-11 px-6" data-testid="wizard-submit-btn">
            {loading ? "Submitting…" : "Submit brief →"}
          </Button>
        )}
      </div>
    </AppShell>
  );
}
